import { Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GenericDialog } from 'src/Common/Components/GenericDialog';
import { CenteredColumn } from 'src/Common/Components/Stacks';
import { SettingsSection } from 'src/SettingsSection/Components/SettingsSection';
import { IntegrationDetailsDrawer } from '../../DeviceIntegrationManagement/Components/IntegrationDetailsDrawer';
import { IntegrationFormDrawer } from '../../DeviceIntegrationManagement/Components/IntegrationFormDrawer';
import { IntegrationGallery } from '../../DeviceIntegrationManagement/Components/IntegrationGallery';
import useDeviceIntegrations from 'src/DeviceIntegrationManagement/Providers/DeviceIntegrationProvider';
import { useSettingsSection } from 'src/SettingsSection/Providers/SettingsSectionProvider';

export const IntegrationsAdminSection = (): React.JSX.Element => {
  const { setDrawerOpen } = useSettingsSection();
  const { selectedIntegration, selectIntegration, deleteIntegration } = useDeviceIntegrations();

  const [mode, setMode] = useState<'edit' | 'view' | 'delete' | undefined>();

  const changeMode = useCallback((toMode: typeof mode) => {
    setMode(toMode);
    setDrawerOpen(toMode !== undefined);
  }, []);

  const dismiss = useCallback(() => {
    changeMode(undefined);
    selectIntegration(undefined);
  }, []);

  const confirmDelete = useCallback(() => {
    if (selectedIntegration) {
      deleteIntegration(selectedIntegration.integrationName, selectedIntegration.coordinate);
    }
  }, [selectedIntegration, deleteIntegration]);

  const drawerContent = useMemo(() => {
    switch (mode) {
      case 'edit':
        return <IntegrationFormDrawer />;
      case 'view':
        return <IntegrationDetailsDrawer onEdit={() => changeMode('edit')} />;
      default:
        return undefined;
    }
  }, [mode]);

  // integration might become unselected while still in a mode that requires selection
  useEffect(() => {
    if (mode === undefined && selectIntegration === undefined) {
      return;
    }

    if (selectIntegration === undefined) {
      switch (mode) {
        case 'view':
        case 'edit':
          changeMode(undefined);
      }
    }
  }, [selectedIntegration, mode]);

  return (
    <SettingsSection
      title={'Integrations'}
      description={'Configure device integrations and monitor their status. Mock data — no changes are persisted.'}
      drawer={drawerContent}
    >
      <IntegrationGallery
        onAdd={() => {
          selectIntegration(undefined);
          changeMode('edit');
        }}
        onEdit={() => {
          changeMode('edit');
        }}
        onDelete={() => changeMode('delete')}
        onClick={() => changeMode('view')}
      />

      {mode === 'delete' && selectedIntegration && (
        <GenericDialog
          open
          fullWidth
          centered
          timedOkButton
          type={'confirmation'}
          maxWidth={'sm'}
          title={'Remove Integration'}
          text={
            <CenteredColumn>
              <Typography>{`Remove "${selectedIntegration.integrationName}" at ${selectedIntegration.coordinate}?`}</Typography>
            </CenteredColumn>
          }
          onClose={dismiss}
          onConfirm={confirmDelete}
        />
      )}
    </SettingsSection>
  );
};
